import { useState } from 'react';

import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { Box, Button, Grid, Typography, buttonClasses } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';

import { DocGenerationSource } from '../../constants/docGenerationSource';
import { OrderTypes } from '../../constants/orderTypeConstants';
import { SigningType } from '../../constants/solexSigningTypes';
import './_base.scss';

interface IProps {
  order?: any;
  documentsSelected: Array<string>;
  setDocumentsSelected: (value: any) => void;
  getDocuments: (filters: Array<string>, orderId: string) => void;
  goGetDocumentsType: (type: string, orderId: string) => void;
  setPrintAllInkSignDocuments?: () => void | undefined;
}

export default function DocumentsDownloaderUploader(props: IProps) {
  const {
    order,
    documentsSelected,
    setDocumentsSelected,
    getDocuments,
    goGetDocumentsType,
    setPrintAllInkSignDocuments,
  } = props;

  const sx = {
    [`& .${buttonClasses.endIcon} > *:nth-of-type(1)`]: {
      fontSize: '32px',
    },
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const EsignCount = order?.OrderDetails?.Documents?.filter(
    (doc: any) => doc?.SigningType === SigningType.ESign
  );
  const InkSignCount = order?.OrderDetails?.Documents?.filter(
    (doc: any) => doc?.SigningType === SigningType.InkSign
  );
  const ENotarizeCount = order?.OrderDetails?.Documents?.filter(
    (doc: any) => doc?.SigningType === SigningType.ENotarize
  );
  const allCount = order?.OrderDetails?.Documents.length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const goGetAllDocuments = () => {
    if (setPrintAllInkSignDocuments) {
      setPrintAllInkSignDocuments();
    }
    setAnchorEl(null);
    getDocuments([], order.Id);
  };

  const goGetDocumentsByType = (type: string, orderId: string) => {
    if (setPrintAllInkSignDocuments && type === SigningType.InkSign) {
      setPrintAllInkSignDocuments();
    }
    setAnchorEl(null);
    goGetDocumentsType(type, orderId);
  };

  const goGetSelectedDocuments = () => {
    const documentIds: Array<string> = [];
    const inkSignDocs: Array<string> = [];

    documentsSelected.map((DocId: string) => {
      const val = DocId.split('%');
      if (setPrintAllInkSignDocuments) {
        let docId = val[1] || null;
        if (docId === 'null') {
          docId = null;
        }
        const id = val[1];
        const foundDoc = InkSignCount.filter(
          (x: any) => x.Id == id && x.SolexDocument.SolexId == docId
        );
        if (foundDoc.length > 0) {
          inkSignDocs.push(foundDoc[0].Name);
        }
      }
      if (val[0] != null) documentIds.push(`${val[1]}`);
    });
    if (
      setPrintAllInkSignDocuments &&
      inkSignDocs.length === InkSignCount.length
    ) {
      setPrintAllInkSignDocuments();
    }
    getDocuments(documentIds, order.Id);
  };

  const columns: GridColDef[] = [
    {
      field: 'Name',
      headerName: 'Name',
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) =>
        `${
          params.row.IsNew ? '*' : params.row.IsFullyExecutedInkSign ? '†' : ''
        } ${params.row.Name || ''}`,
    },
    {
      field: 'Type',
      headerName: 'Type',
      disableColumnMenu: true,
      sortable: false,
      flex: 0.5,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.SigningType === 'NotApplicable'
          ? '-'
          : params.row.SigningType,
    },
  ];

  return (
    // If documents are not distributed we don't want to allow the user to download the documents
    <Grid className={!order?.DocumentLinksSent ? 'cancel-style' : ''} item>
      <Grid container>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '10px 0px',
                borderBottom: 'solid 2px black',
              }}>
              <Button
                data-testid='action-button'
                id='demo-positioned-button'
                aria-controls={open ? 'demo-positioned-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                size='large'
                sx={sx}
                endIcon={<ArrowDropDown />}
                className='view-btn'>
                View/Print Documents by Type
              </Button>
              {order?.OrderType?.DocGen !== DocGenerationSource.FA && (
                // We disable this for now, pending solution for FA
                <Button
                  className='view-btn'
                  disabled={documentsSelected?.length === 0}
                  onClick={goGetSelectedDocuments}
                  variant='text'>
                  View/Print Selected
                </Button>
              )}
              <Menu
                id='demo-positioned-menu'
                aria-labelledby='demo-positioned-button'
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 0,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                <MenuItem onClick={goGetAllDocuments}>
                  View/Print All ({allCount}){' '}
                </MenuItem>
                <div>
                  <MenuItem
                    disabled={EsignCount.length === 0}
                    onClick={() =>
                      goGetDocumentsByType(SigningType.ESign, order.Id)
                    }>
                    View/Print Esign ({EsignCount.length})
                  </MenuItem>
                  {order.OrderType.Type === OrderTypes.IPEN && (
                    <MenuItem
                      disabled={ENotarizeCount.length === 0}
                      onClick={() =>
                        goGetDocumentsByType(SigningType.ENotarize, order.Id)
                      }>
                      View/Print ENotarize ({ENotarizeCount.length})
                    </MenuItem>
                  )}
                  {order.OrderType.Type === OrderTypes.HybridEClose && (
                    <MenuItem
                      disabled={InkSignCount.length === 0}
                      onClick={() =>
                        goGetDocumentsByType(SigningType.InkSign, order.Id)
                      }>
                      View/Print Ink Sign ({InkSignCount.length})
                    </MenuItem>
                  )}
                </div>
              </Menu>
            </Box>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                sx={{ border: 'none', boxShadow: 'none' }}
                checkboxSelection={
                  order?.OrderType?.DocGen !== DocGenerationSource.FA
                }
                getRowId={(r) => `${r.SolexDocument?.Cxid}%${r?.Id}%${r?.Name}`} // We have to do this because some docs will share the same Cxid. This makes sure the row id is unique!
                rows={order.OrderDetails.Documents}
                columns={columns}
                autoHeight={false}
                onRowSelectionModelChange={(selections: any) =>
                  setDocumentsSelected(selections)
                }
                hideFooterPagination
              />
            </Box>
            <Box pt={1} sx={{ borderTop: 'solid 1px rgba(0, 0, 0, 0.12);' }}>
              <Typography variant='subtitle2'>* Upload Documents</Typography>
              {order.OrderType.Type === OrderTypes.HybridEClose && (
                <Typography variant='subtitle2'>
                  † Fully Executed Ink Sign Documents
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
